<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { KsDialog, KsDrawer } from '@aschehoug/kloss'

const props = withDefaults(defineProps<{
  title?: string
  body?: string
  icon?: IconDefinition
  defaultOpen?: boolean
  dismissable?: boolean
  drawerPosition?: 'left' | 'right' | 'top' | 'bottom'
  drawerSize?: string
}>(), {
  dismissable: false,
})

const emit = defineEmits(['confirm', 'cancel', 'close'])

const open = ref(props.defaultOpen ?? false)

defineExpose({
  isOpen: open,
  show() {
    open.value = true
  },
  hide() {
    open.value = false
  },
})

function handleClose() {
  open.value = false
  emit('close')
}

function handleCancel() {
  open.value = false
  emit('cancel')
}

function handleConfirm() {
  emit('confirm')
}

const { theme } = useTheme()
// KsDrawer forwards only header and body slots; it cannot render this component's footer actions.
const modalComponent = computed(() => props.drawerPosition ? KsDrawer : KsDialog)
</script>

<template>
  <component
    :is="modalComponent"
    class="px-2xl py-xl !rounded-lg text-primary reading-dark-scope"
    :class="theme"
    :dismissable="dismissable"
    :open="open"
    :title="title ? title : ''"
    :position="drawerPosition"
    :size="drawerSize"
    @close="handleClose"
  >
    <template #header>
      <div
        v-if="icon || $slots.icon" class="w-2xl aspect-square flex justify-center items-center rounded-lg bg-blurple-100"
      >
        <Icon v-if="icon" :icon="icon" size="xl" />
      </div>
      <slot v-else name="header" />
    </template>
    <template #body>
      <p v-if="body" v-text="body" />
      <slot v-else name="body" />
    </template>
    <template #footer>
      <div v-if="!$slots.footer" class="flex gap-2xs">
        <Button variant="tertiary" @click="handleCancel">
          Avbryt
        </Button>
        <Button variant="primary" @click="handleConfirm">
          Bekreft
        </Button>
      </div>
      <slot name="footer" />
    </template>
  </component>
</template>

<style scoped>
:deep(.ks-dialog) {
  &.reading-dark-scope {
    color: var(--text-primary);
  }

  .ks-dialog-header > h1 {
    @apply text-4xl;
  }

  /* FIXME: Would be best to use flex+gap directly on the KsDialog instead of padding on header + footer, but for some reason the flex gap disappears during the closing animation of the Modal */

  .ks-dialog-header {
    @apply p-0 pb-m w-fit;
  }

  .ks-dialog-body {
    @apply p-0;
  }

  .ks-dialog-footer {
    @apply p-0 pt-m;
  }
}

.ks-dialog-backdrop {
  &:has(.blurple) {
    @apply bg-blurple-500/70;
  }

  &:has(.carbon) {
    @apply bg-carbon-500/70;
  }

  &:has(.moss) {
    @apply bg-moss-500/70;
  }
}
</style>
